// ─── Angular Core ───────────────────────────────────────────────
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";

// ─── Angular Material ───────────────────────────────────────────
import { MatDialog, MAT_DIALOG_DATA } from "@angular/material/dialog";

// ─── RxJS ───────────────────────────────────────────────────────
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

// ─── Project Types ──────────────────────────────────────────────
import { Header, Rule } from "@aptsi-types";

// ─── App Services ───────────────────────────────────────────────
import { KeycloakStateService } from "src/app/core/service/utility.service";
import { RuleService } from "../../rules.service";
import { TimeFormatService } from "../../../shared/service/time-format.service";

// ─── App Components ─────────────────────────────────────────────
import { DeleteDialogComponent } from "../../../shared/dialogs/delete-dialog/delete-dialog.component";
import { EnvironmentService } from "src/app/core/service/environment.service";


@Component({
  selector: "app-rules-search",
  templateUrl: "./rules-search.component.html",
  styleUrls: ["./rules-search.component.scss"],
})
export class RulesSearchComponent implements OnInit {
  // ─── Pagination ───────────────────────────────────────────────
  currentPage = 1;
  itemsPerPage = 7;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ─── User Context ─────────────────────────────────────────────
  sessionId = this.keycloakStateService.getSessionId();
  userId = this.keycloakStateService.getUserId();

  // ─── Search & Filtering ──────────────────────────────────────
  searchText: string = "";
  filterByHeader: string = "all";
  isSearch = false;
  searchTextChanged = new Subject<string>();

  // ─── Data ─────────────────────────────────────────────────────
  rules: Rule[] = [];
  filteredData: Rule[] = [];
  searchedData: Rule[] = [];

  // ─── UI Configuration ─────────────────────────────────────────
  headers: Header[] = [
    {
      name: "Rule Id",
      property: "ruleId",
      showInDropdown: true,
      description: "Unique identifier for the rule",
    },
    {
      name: "Rule Name",
      property: "name",
      showInDropdown: true,
      description: "Name of the rule",
    },
    {
      name: "Rule Type",
      property: "ruleType",
      showInDropdown: true,
      description: "Type of the rule",
    },
    {
      name: "Last Updated",
      property: "timestamp",
      showInDropdown: false,
      description: "Timestamp of the most recent update",
    },
  ];

  buttonConfig = {
    update: true,
    delete: true,
    import: false,
  };

  constructor(
    private router: Router,
    private ruleService: RuleService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private keycloakStateService: KeycloakStateService,
    private timeFormatService: TimeFormatService,
    public environmentService: EnvironmentService 
  ) {}

  // ──────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
    
    
    await this.fetchRules();

    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((text) => {
        this.searchText = text;
        this.searchRules();
      });

    this.cdr.detectChanges();
  }

  // ──────────────────────────────────────────────────────────────
  // Getters
  // ──────────────────────────────────────────────────────────────
  get paginatedData(): Rule[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.isSearch
      ? this.searchedData.slice(start, end)
      : this.filteredData.slice(start, end);
  }

  // ──────────────────────────────────────────────────────────────
  // Event Handlers
  // ──────────────────────────────────────────────────────────────
  changePage(page: number): void {
    this.currentPage = page;
    this.totalPages = this.isSearch
      ? this.totalPagesSearch
      : this.totalPagesNormal;
    this.isSearch ? this.fetchRuleSearch() : this.fetchRules();
  }

  onSearchTextChange(searchValue: string): void {
    this.searchTextChanged.next(searchValue);
  }

  handleAction(event: {
    action: string;
    rowIndex: number;
    rowData: Rule;
  }): void {
    if (event.action === "update") {
      this.updateRule(event.rowIndex);
    } else if (event.action === "delete") {
      this.deleteRule(event.rowIndex);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Navigation
  // ──────────────────────────────────────────────────────────────
  updateRule(index: number): void {
    const ruleId = this.isSearch
      ? this.searchedData[index].ruleId
      : this.filteredData[index].ruleId;
    this.router.navigate([
      "ada-access/auth-tools/policy-authoring/rules/update",
      ruleId,
    ]);
  }

  createRules(): void {
    this.router.navigate([
      "ada-access/auth-tools/policy-authoring/rules/create",
    ]);
  }

  back(): void {
    if (this.isSearch) {
      this.isSearch = false;
      this.fetchRules();
    } else {
      this.router.navigate(["ada-access/auth-tools/policy-authoring"]);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Data Fetching
  // ──────────────────────────────────────────────────────────────
  async fetchRules(): Promise<void> {
    try {
      const response = await this.ruleService
        .getRules(this.currentPage - 1, this.sessionId, this.userId)
        .toPromise();
      this.formatAndSetRules(response, false);
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  }

  async fetchRuleSearch(): Promise<void> {
    try {
      const response = await this.ruleService
        .getRuleSearch(
          this.currentPage - 1,
          this.searchText,
          this.filterByHeader
        )
        .toPromise();
      this.formatAndSetRules(response, true);
    } catch (error) {
      console.error("Error fetching rule search:", error);
    }
  }

  private formatAndSetRules(response: any[], isSearch: boolean): void {
    const formatted = response.map((rule) => ({
      ...rule,
      timestamp: this.timeFormatService.formatDate(rule.timestamp),
    }));

    if (isSearch) {
      this.searchedData = formatted;
      this.totalPagesSearch = response[0]?.totalPages || 0;
    } else {
      this.filteredData = formatted;
      this.totalPagesNormal = response[0]?.totalPages || 0;
    }

    this.updatePagination(response);
  }

  private updatePagination(response: any[]): void {
    if (response.length > 0 && response[0].totalPages) {
      const totalPages = response[0].totalPages;
      const currentPage = response[0].currentPage + 1;

      if (this.isSearch) {
        this.totalPagesSearch = totalPages;
      } else {
        this.totalPagesNormal = totalPages;
      }

      this.totalPages = totalPages;
      this.currentPage = currentPage;
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Search
  // ──────────────────────────────────────────────────────────────


// async searchRules(): Promise<void> {
//   try {
//     if (this.searchText && this.searchText.trim()) {

//       this.isSearch = true;
//       this.currentPage = 1;

//       await this.fetchRuleSearch();

//       const selectedHeader = this.headers.find(
//         (h) =>
//           h.property === this.filterByHeader &&
//           h.showInDropdown
//       );

//       if (selectedHeader) {

//         this.searchedData = this.searchedData.filter((rule) => {
//           const value = rule[selectedHeader.property];

//           return value
//             ?.toString()
//             .toLowerCase()
//             .includes(this.searchText.toLowerCase());
//         });

//         this.searchedData.sort((a, b) =>
//           a[selectedHeader.property]
//             ?.toString()
//             .localeCompare(
//               b[selectedHeader.property]?.toString()
//             )
//         );

//       } else {
//         this.searchedData = [];
//       }

//     } else {

//       this.isSearch = false;
//       this.currentPage = 1;

//       await this.fetchRules();
//     }

//   } catch (error) {
//     console.error("Error during search:", error);
//   }
// }


  async searchRules(): Promise<void> {

    try {

      if (this.searchText?.trim()) {

        this.isSearch = true;
        this.currentPage = 1;

        if (!this.filterByHeader) {
          this.filterByHeader = 'all';
        }

        // Backend handles:
        // search
        // pagination
        // filtering
        await this.fetchRuleSearch();

      } else {

        this.isSearch = false;
        this.currentPage = 1;

        await this.fetchRules();
      }

    } catch (error) {

      console.error(
        'Error during search:',
        error
      );
    }
  }

  

  // ──────────────────────────────────────────────────────────────
  // Delete
  // ──────────────────────────────────────────────────────────────
  async deleteRule(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Are you sure?",
          message: "Do you really want to delete this Rule?",
          isConfirm: false,
        },
      });

      const result = await dialogRef.afterClosed().toPromise();
      if (!result) return;

      const ruleId = this.isSearch
        ? this.searchedData[index].ruleId
        : this.filteredData[index].ruleId;
      await this.ruleService.deleteRule(ruleId).toPromise();

      this.isSearch ? this.fetchRuleSearch() : this.fetchRules();

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Success",
          message: "The Rule has been deleted successfully.",
          isConfirm: true,
        },
      });
    } catch (error) {
      console.error("Error deleting Rule:", error);
    }
  }
}
